import type { ControlBridgeQueueItem, ControlBridgeViewModel, ControlBridgeWaypointItem } from './deriveViewModel';
import { displayValue } from './deriveViewModel';
import { itemIdsEqual } from '../../utils/itemIdPlane';

export interface GuidanceImprovementRow {
  itemId: string;
  title: string;
  statusPhrase: string;
  openActivities: number;
  openIssues: number;
  priority?: string;
  plankDescription?: string;
  futureCondition?: string;
  resourceAssignment?: string;
  assumptionText?: string;
  estimatedDuration?: string;
  startDate?: string;
  endDate?: string;
  duration?: string;
}

export interface GuidanceWaypointRow {
  waypointId: string;
  title: string;
  statusPhrase: string;
  sequenceOrder?: number;
  improvementsOpen: number;
  improvementsTotal: number;
  activitiesOpen: number;
  activitiesTotal: number;
  issuesOpen: number;
  issuesTotal: number;
  improvements: GuidanceImprovementRow[];
}

export interface GuidanceBridgeSessionGroup {
  sessionKey: string;
  sessionLabel: string;
  waypoints: GuidanceWaypointRow[];
}

function countPair(open: number | undefined, total?: number | undefined): { open: number; total: number; label: string } {
  const o = typeof open === 'number' ? open : 0;
  const t = typeof total === 'number' ? total : o;
  return { open: o, total: t, label: `${o} / ${t}` };
}

function improvementNarrativeFromItem(item: ControlBridgeQueueItem): Pick<
  GuidanceImprovementRow,
  | 'plankDescription'
  | 'futureCondition'
  | 'resourceAssignment'
  | 'assumptionText'
  | 'estimatedDuration'
  | 'startDate'
  | 'endDate'
  | 'duration'
> {
  return {
    plankDescription: item.plankDescription,
    futureCondition: item.futureCondition,
    resourceAssignment: item.resourceAssignment,
    assumptionText: item.assumptionText,
    estimatedDuration: item.estimatedDuration,
    startDate: item.startDate,
    endDate: item.endDate,
    duration: item.duration,
  };
}

export function inferImprovementsForWaypoint(
  waypoint: ControlBridgeWaypointItem,
  eligibleWork: ControlBridgeQueueItem[],
  openIssues: ControlBridgeQueueItem[]
): GuidanceImprovementRow[] {
  const waypointId = waypoint.itemId;
  if (!waypointId) return [];

  const rows: GuidanceImprovementRow[] = [];

  for (const item of [...eligibleWork, ...openIssues]) {
    if (item.waypointRef !== waypointId && item.improvementRef !== waypointId) {
      continue;
    }
    const key = item.improvementRef ?? item.itemId;
    const isIssue = openIssues.some((i) => itemIdsEqual(i.itemId, item.itemId));
    /** Improvement row itself is not a child activity for decompose / Create visibility. */
    const isSelfImprovement = itemIdsEqual(item.itemId, key);
    const existing = rows.find((row) => itemIdsEqual(row.itemId, key));
    if (existing) {
      if (isIssue) existing.openIssues += 1;
      else if (!isSelfImprovement) existing.openActivities += 1;
      Object.assign(existing, {
        plankDescription: existing.plankDescription ?? item.plankDescription,
        futureCondition: existing.futureCondition ?? item.futureCondition,
        resourceAssignment: existing.resourceAssignment ?? item.resourceAssignment,
        assumptionText: existing.assumptionText ?? item.assumptionText,
        estimatedDuration: existing.estimatedDuration ?? item.estimatedDuration,
        startDate: existing.startDate ?? item.startDate,
        endDate: existing.endDate ?? item.endDate,
        duration: existing.duration ?? item.duration,
      });
      continue;
    }
    rows.push({
      itemId: key,
      title: item.improvementTitle ?? item.title,
      statusPhrase: item.statusPhrase,
      openActivities: isIssue || isSelfImprovement ? 0 : 1,
      openIssues: isIssue ? 1 : 0,
      priority: item.priority,
      ...improvementNarrativeFromItem(item),
    });
  }

  return rows;
}

function improvementsFromManifestWbs(
  waypointId: string,
  guidanceWbs: ControlBridgeQueueItem[]
): GuidanceImprovementRow[] {
  return guidanceWbs
    .filter((row) => row.waypointRef === waypointId)
    .map((row) => ({
      itemId: row.improvementRef ?? row.itemId,
      title: row.improvementTitle ?? row.title,
      statusPhrase: row.statusPhrase,
      openActivities: row.activeActivities ?? 0,
      openIssues: row.openIssues ?? 0,
      priority: row.priority,
      plankDescription: row.plankDescription,
      futureCondition: row.futureCondition,
      resourceAssignment: row.resourceAssignment,
      assumptionText: row.assumptionText,
      estimatedDuration: row.estimatedDuration,
      startDate: row.startDate,
      endDate: row.endDate,
      duration: row.duration,
    }));
}

/** Merge guidance-wbs rows with queue inference; fallback when WBS cap starves a waypoint. */
export function resolveImprovementsForWaypoint(
  waypoint: ControlBridgeWaypointItem,
  vm: ControlBridgeViewModel
): GuidanceImprovementRow[] {
  const fromWbs =
    vm.guidanceWbsQueue.length > 0
      ? improvementsFromManifestWbs(waypoint.itemId, vm.guidanceWbsQueue)
      : [];
  const fromQueue = inferImprovementsForWaypoint(waypoint, vm.eligibleWork, vm.openIssues);

  if (vm.guidanceWbsQueue.length === 0) {
    return fromQueue;
  }

  if (fromWbs.length > 0) {
    return fromWbs;
  }

  const impTotal =
    typeof waypoint.improvementsTotal === 'number'
      ? waypoint.improvementsTotal
      : typeof waypoint.openImprovements === 'number'
        ? waypoint.openImprovements
        : 0;
  if (impTotal > 0) {
    return fromQueue;
  }

  return [];
}

function waypointRow(
  waypoint: ControlBridgeWaypointItem,
  vm: ControlBridgeViewModel
): GuidanceWaypointRow {
  const imp = countPair(waypoint.openImprovements, waypoint.improvementsTotal);
  const act = countPair(waypoint.activeActivities, waypoint.activitiesTotal);
  const iss = countPair(waypoint.openIssues, waypoint.issuesTotal);
  const improvements = resolveImprovementsForWaypoint(waypoint, vm);

  return {
    waypointId: waypoint.itemId,
    title: waypoint.title,
    statusPhrase: waypoint.statusPhrase,
    sequenceOrder: waypoint.sequenceOrder,
    improvementsOpen: imp.open,
    improvementsTotal: imp.total,
    activitiesOpen: act.open,
    activitiesTotal: act.total,
    issuesOpen: iss.open,
    issuesTotal: iss.total,
    improvements,
  };
}

export function deriveGuidanceSessions(vm: ControlBridgeViewModel): GuidanceBridgeSessionGroup[] {
  const sessionLabel = displayValue(vm.rollup.bridgeSession);
  const sessionKey =
    typeof vm.rollup.bridgeSession === 'string'
      ? vm.rollup.bridgeSession
      : sessionLabel !== 'Not captured'
        ? sessionLabel
        : 'default-session';

  const waypoints = vm.waypoints.length > 0 ? vm.waypoints : vm.guidanceTimeline;

  return [
    {
      sessionKey,
      sessionLabel,
      waypoints: waypoints.map((wp) => waypointRow(wp, vm)),
    },
  ];
}

export function formatOpenTotal(open: number, total: number): string {
  return `${open} / ${total}`;
}
